/* eslint-disable react/display-name,no-unused-vars */
import axios from 'helpers/axios';
import {
  useCancelToken,
  useCompany,
  useDashboard,
  useMutableRef,
  useOnFirstRender,
  useOpenState,
  useStateRef,
  useToasts,
} from 'hooks';
import useMediaQuery from 'hooks/useMediaQuery';
import React from 'react';
import { useDrop } from 'react-dnd';
import {
  DataTable,
  DataTableCell,
  DataTableColumn,
  DropDownMenu,
  DropDownMenuItem,
  EmailModal,
  FloatingMenuButton,
  FloatingMenuChildButton,
  Icon,
  IconButton,
  JobDot,
} from 'ui/components/shared';
import { getNumberOfPages, recalculatePage } from 'ui/components/shared/DataTable/helpers';
import { Container, ModalPosition } from 'ui/components/styled';
import { Modal } from 'ui/components/styled/NewModal';
import { ConfirmationModal } from 'ui/components/styled/NewModal/ConfirmationModal';
import Text from 'ui/components/styled/Text';
import device from 'ui/theme/device';
import { AddCategory, AddCustomer, AddJob, EditCategory, EditCustomer } from '../forms';
import { buildFilter } from './helper';

export const ListView = React.forwardRef(
  (
    {
      headerActions,
      isFullScreenActive,
      filterValue,
      openMobileFilter,
      activeStatus = '',
      onFilterChange,
    },
    ref
  ) => {
    const [data, setData] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [totalRows, setTotalRows] = React.useState(0);
    const [perPage, setPerPage] = React.useState(10);
    const [sort, setSort] = React.useState({ column: '', order: '' });
    const [search, setSearch] = React.useState(filterValue);
    const [advancedSearchOpen, setAdvancedSearchOpen] = React.useState(false);

    const [modal, openModal, closeModal] = useOpenState();
    const { errorToast, successToast, warningToast, removeAllToasts } = useToasts();
    const { activeCompany, isCompanyAdmin, companiesLoaded } = useCompany();
    const { refreshStatistics, updateJobs, moveJob } = useDashboard();
    const readonly = !isCompanyAdmin();
    const dataRef = useStateRef(data);
    const categoriesRef = useStateRef(categories);
    const [currentPage, setCurrentPage] = useMutableRef(1);
    const customersCancelToken = useCancelToken();
    const categoriesCancelToken = useCancelToken();
    const isSmall = useMediaQuery(device.small);
    const initialColumns = [
      {
        name: 'Customer',
        selector: 'name',
        cell: (row) => (
          <DataTableCell title={row.name} textOverflow="none">
            {row.name}
          </DataTableCell>
        ),
        sortable: true,
      },
      {
        name: <DataTableColumn noHover>Actions</DataTableColumn>,
        right: true,
        button: true,
        ignoreRowClick: true,
        minWidth: '120px',
        sortable: false,
        omit: activeCompany && readonly,
        cell: (row) =>
          !readonly && (
            <>
              <IconButton
                title="Add monitor"
                icon="plus"
                iconProps={{ size: '12px' }}
                size="26px"
                mx="xxs"
                onClick={() => {
                  if (categoriesRef.current.length === 0) {
                    warningToast('Please add a category first.');
                  } else {
                    openModal('add-job', { customer: row });
                  }
                }}
              />
              <IconButton
                title="Edit"
                variant="transparent"
                icon="edit"
                mx="xxs"
                onClick={() => openModal('edit-customer', { customer: row })}
              />
              <IconButton
                title="Delete"
                variant="transparent"
                icon="trash"
                mx="xxs"
                onClick={() => openModal('confirm-remove-customer', { customer: row })}
              />
            </>
          ),
      },
    ];
    const [columns, setColumns] = React.useState(initialColumns);
    const isFirstRender = useOnFirstRender();

    React.useEffect(() => {
      if (search !== filterValue) {
        onFilterChange(search);
      }
    }, [search]);

    const fetchCustomers = async (page, silent = false, keepLoading = false) => {
      if (!silent) setLoading(true);

      customersCancelToken.cancelPreviousRequest();

      try {
        const skip = page > 1 ? (page - 1) * perPage : 0;
        const response = await axios.get(
          `/customers/with-jobs/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$cid=${activeCompany}&$status=${activeStatus}`,
          {
            cancelToken: customersCancelToken.newCancelToken(),
            params: {
              $search: advancedSearchOpen ? buildFilter(search) : search,
            },
          }
        );

        if (response.status === 200) {
          setData(response.data.value);
          setTotalRows(response.data.count);
        }
      } catch (error) {
        if (silent || customersCancelToken.isCancel(error)) return;
        errorToast({ error, default: 'Error: Fetch Customers.' });
      }

      if (!keepLoading) setLoading(false);
    };

    const fetchCategories = async (silent = false) => {
      if (!silent) setLoading(true);

      categoriesCancelToken.cancelPreviousRequest();

      try {
        const response = await axios.get(
          `/jobs/categories/?$top=-1&$cid=${activeCompany}&$select=name,company`,
          { cancelToken: categoriesCancelToken.newCancelToken() }
        );
        if (response?.status === 200) {
          const categories = response.data.value;
          setCategories(categories);
          updateColumns(categories);
        }
      } catch (error) {
        if (categoriesCancelToken.isCancel(error)) return;
      }

      setLoading(false);
    };

    const updateColumns = (categories) => {
      const categoriesColumns = categories.map((category) => ({
        name: renderCategoryColumn(category),
        sortable: false,
        cell: (row) => renderJobs({ customer: row, category }),
      }));
      setColumns([initialColumns[0], ...categoriesColumns, ...initialColumns.slice(1)]);
    };

    const updateData = (data) => {
      setData(data);
      updateColumns(categoriesRef.current);
    };

    const handleRowsPerPageChange = async (newPerPage, page) => {
      setLoading(true);
      setCurrentPage(page);

      try {
        const skip = page > 1 ? (page - 1) * newPerPage : 0;
        const response = await axios.get(
          `/customers/with-jobs/?$top=${newPerPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$cid=${activeCompany}&$status=${activeStatus}`,
          {
            params: {
              $search: advancedSearchOpen ? buildFilter(search) : search,
            },
          }
        );

        if (response.status === 200) {
          setData(response.data.value);
          setPerPage(newPerPage);
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Rows Change.' });
      }

      setLoading(false);
    };

    const handlePageChange = (page) => {
      fetchData(page);
    };

    const handleSort = async (column, sortDirection) => {
      setLoading(true);

      const sortColumn = column?.selector || '';
      const sortOrder = sortColumn ? sortDirection : '';
      setSort({ column: sortColumn, order: sortOrder });

      debugger; // eslint-disable-line

      try {
        const skip = 0;

        const response = await axios.get(
          `/customers/with-jobs/?$top=${perPage}&$skip=${skip}&$orderby=${sortColumn}&$order=${sortOrder}&$cid=${activeCompany}&$status=${activeStatus}`,
          {
            params: {
              $search: advancedSearchOpen ? buildFilter(search) : search,
            },
          }
        );

        if (response.status === 200) {
          setData(response.data.value);
          setTotalRows(response.data.count);
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Sort Customers.' });
      }

      setLoading(false);
    };

    const handleFilter = async (value) => {
      setLoading(true);
      setSearch(value);

      customersCancelToken.cancelPreviousRequest();
      try {
        const skip = 0;
        const response = await axios.get(
          `/customers/with-jobs/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$cid=${activeCompany}&$status=${activeStatus}`,
          {
            cancelToken: customersCancelToken.newCancelToken(),
            params: {
              $search: advancedSearchOpen ? buildFilter(value) : value,
            },
          }
        );

        if (response.status === 200) {
          setData(response.data.value);
          setTotalRows(response.data.count);
          refreshStatistics(value);
        }
      } catch (error) {
        if (customersCancelToken.isCancel(error)) return;
        errorToast({ error, default: 'Error: Filter Customers.' });
      }

      setLoading(false);
    };

    const refresh = ({ newRowsCount, silent = false } = {}) => {
      if (newRowsCount > 0) {
        const updatedPage = getNumberOfPages(newRowsCount, perPage);
        const recalculatedPage = recalculatePage(currentPage.current, updatedPage);
        if (recalculatedPage !== currentPage.current) {
          setCurrentPage(recalculatedPage);
        }
      }

      fetchData(currentPage.current, silent);
      refreshStatistics(search);
    };

    const archiveCustomer = async (customer) => {
      try {
        const response = await axios.post(`/customers/${customer.id}/archive`);

        if (response.status === 200) {
          removeAllToasts();
          successToast(`Customer “${customer.name}” has been successfully archived.`);
          refresh({ newRowsCount: totalRows - 1 });
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Archive Customer.' });
      }

      closeModal();
    };

    const archiveCategory = async (category) => {
      try {
        const response = await axios.post(`/jobs/categories/${category.id}/archive`);

        if (response.status === 200) {
          removeAllToasts();
          successToast(`Category “${category.name}” has been successfully archived.`);
          refresh();
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Archive Category.' });
      }

      closeModal();
    };

    const fetchData = async (page = 1, silent = false) => {
      setCurrentPage(page);
      await fetchCustomers(page, silent, true);
      fetchCategories(silent);
    };

    const handleRealtimeUpdates = (eventData) => {
      // updateJobs(eventData, data, updateData); // doesn't update sort order, new added jobs, nor recent email tooltip
      fetchData(currentPage.current, true);
      refreshStatistics(search);
    };

    // allow us to call defined functions from parent
    React.useImperativeHandle(ref, () => ({
      refresh,
      handleRealtimeUpdates,
    }));

    React.useEffect(() => {
      if (companiesLoaded) {
        fetchData();
      }
    }, [companiesLoaded, activeCompany]);

    React.useEffect(() => {
      if (!isFirstRender) {
        setCurrentPage(1);
        refresh();
      }
    }, [activeStatus]);

    const renderCategoryColumn = (category) => (
      <Container flexDirection="row" alignItems="center">
        {!readonly ? (
          <DropDownMenu
            label={category.name}
            labelOnly
            fixedLeftSpacing={10}
            listContainerProps={{ mt: '6px' }}
          >
            <DropDownMenuItem onClick={() => openModal('edit-category', { category })}>
              <Icon icon="edit" size="14px" mr="7px" /> Edit
            </DropDownMenuItem>
            <DropDownMenuItem onClick={() => openModal('confirm-remove-category', { category })}>
              <Icon icon="trash" size="16px" mr="7px" /> Delete
            </DropDownMenuItem>
          </DropDownMenu>
        ) : (
          <span>{category.name}</span>
        )}
      </Container>
    );

    const renderJobs = ({ customer, category }) => {
      const [, drop] = useDrop(
        () => ({
          accept: 'job',
          drop(item) {
            const target = { customer, category };
            moveJob(item, target, dataRef.current, updateData);
            return undefined;
          },
        }),
        [moveJob]
      );

      return (
        <DataTableCell ref={drop} expanded wrapped wrapperProps={{ mr: '-5px' }} py="xs">
          {customer.jobs
            ?.filter(({ category: categoryId }) => categoryId === category.id)
            .map((job, index) => (
              <JobDot
                key={index}
                job={job}
                onTooltipReadMoreClick={() => openModal('view-email', { email: job.lastEmail })}
                isDraggable={!readonly}
                hasTooltip
              />
            ))}
        </DataTableCell>
      );
    };

    return (
      <>
        <DataTable
          variant="outline"
          columns={columns}
          data={data}
          progressPending={loading}
          paginationPerPage={perPage}
          paginationTotalRows={totalRows}
          paginationCurrentPage={currentPage.current}
          paginationHeight="86px"
          onRowsPerPageChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
          onPageReset={setCurrentPage}
          onSort={handleSort}
          filterPlaceholder="Search"
          filterValue={search}
          openMobileFilter={openMobileFilter}
          onFilter={handleFilter}
          subHeaderActions={headerActions}
          subHeaderDisposition="actions-first"
          keepHeadRowPointerEvents={categories?.length > 0}
          rowHeight={isSmall ? '45px' : '64px'}
          isSmall={isSmall}
          zIndex={1}
          advancedSearch
          advancedSearchOpen={advancedSearchOpen}
          setAdvancedSearchOpen={setAdvancedSearchOpen}
        />
        <Modal
          isOpen={modal.isOpen('add-customer', 'edit-customer', 'add-category', 'edit-category')}
          onRequestClose={closeModal}
          position={ModalPosition.TOP_CENTER}
          adjustPosition={!isFullScreenActive}
          top="25%"
          width="40%"
          maxWidth="650px"
        >
          {modal.isOpen('add-customer') && <AddCustomer onClose={closeModal} onSuccess={refresh} />}
          {modal.isOpen('edit-customer') && (
            <EditCustomer
              customer={modal.params?.customer}
              onClose={closeModal}
              onSuccess={refresh}
            />
          )}
          {modal.isOpen('add-category') && <AddCategory onClose={closeModal} onSuccess={refresh} />}
          {modal.isOpen('edit-category') && (
            <EditCategory
              category={modal.params?.category}
              onClose={closeModal}
              onSuccess={refresh}
            />
          )}
        </Modal>
        <Modal
          isOpen={modal.isOpen('add-job')}
          onRequestClose={closeModal}
          position={ModalPosition.TOP_CENTER}
          adjustPosition={!isFullScreenActive}
          top="7%"
          width="40%"
          minWidth="700px"
          resize="horizontal"
        >
          {modal.isOpen('add-job') && (
            <AddJob customer={modal.params?.customer} onClose={closeModal} onSuccess={refresh} />
          )}
        </Modal>
        <ConfirmationModal
          show={modal.isOpen('confirm-remove-customer')}
          onClose={closeModal}
          onConfirm={() => archiveCustomer(modal.params?.customer)}
          adjustPosition={!isFullScreenActive}
          confirmButtonText="Archive"
          data-testid="remove-customer-modal"
        >
          <Text>
            {modal?.params?.customer?.name} and all its monitors will be archived, if you truly want
            to delete it, you should do so from the archives.
          </Text>
        </ConfirmationModal>
        <ConfirmationModal
          show={modal.isOpen('confirm-remove-category')}
          onClose={closeModal}
          onConfirm={() => archiveCategory(modal.params?.category)}
          adjustPosition={!isFullScreenActive}
          confirmButtonText="Archive"
          data-testid="remove-category-modal"
        >
          <Text>
            {modal?.params?.category?.name} and all its monitors will be archived, if you truly want
            to delete it, you should do so from the archives.
          </Text>
        </ConfirmationModal>
        <EmailModal
          show={modal.isOpen('view-email')}
          onClose={closeModal}
          email={modal.params?.email}
        />
        {(!activeCompany || !readonly) && (
          <FloatingMenuButton position="fixed" right="1em" bottom="1em">
            <FloatingMenuChildButton onClick={() => openModal('add-customer')}>
              Add Customer
            </FloatingMenuChildButton>
            <FloatingMenuChildButton onClick={() => openModal('add-category')}>
              Add Category
            </FloatingMenuChildButton>
          </FloatingMenuButton>
        )}
      </>
    );
  }
);
